// Outside services step — vendor-referenced version of the old "Finishing" idea,
// surfaced here as its own step so EVERY non-purchased part type can attach
// outside services (laser cut, plating, heat treat, polish, etc).
//
// Storage path is `part.outsideServices[]` (OutsideService per crmTypes).
// New rows are written with the preferred shape:
//   { serviceType: VendorServiceType, vendorId: string, type: <label>, vendor: <name>, ... }
// We keep the legacy `type` + `vendor` strings populated alongside so legacy
// consumers (PartDetailPage, ReviewStep, quotePricing) keep working untouched.
//
// Vendor picker: calls listVendors({service: selectedType}) and narrows to
// vendors that do that service. "+ create new vendor" link routes to
// /settings#vendors so the user can spin one up without losing wizard state.

import { useEffect, useMemo, useState } from 'react';
import type { StepProps } from '../stepTypes';
import type { OutsideService, VendorServiceType, Vendor } from '@/shared/crmTypes';
import { VENDOR_SERVICE_LABELS } from '@/shared/crmTypes';
import { listVendors, getVendor } from '@/shared/vendors';

// Ordered list of service-type options for the select. Pull from the
// taxonomy + label map so we stay in lockstep with crmTypes.
const SERVICE_TYPE_OPTIONS: VendorServiceType[] = [
  'laser_cutting',
  'plasma_cutting',
  'waterjet_cutting',
  'painting',
  'powder_coating',
  'anodizing',
  'black_oxide',
  'heat_treat',
  'grinding',
  'broaching',
  'edm',
  'plating_zinc',
  'plating_nickel',
  'plating_chrome',
  'passivation',
  'deburring',
  'polishing',
  'tumbling',
  'silk_screen',
  'engraving',
  'material_supply',
  'other',
];

interface AddFormState {
  serviceType: VendorServiceType;
  /** Freeform name when serviceType === 'other'. */
  otherLabel: string;
  vendorId: string;
  /** Manual vendor name fallback (used when no vendorId selected). */
  vendorName: string;
  /** Whether the cost rolls in per-piece or as a flat per-batch lot charge. */
  costMode: 'per_piece' | 'lot_charge';
  setupCost: string;
  perPieceCost: string;
  lotCharge: string;
  supplierTaxPct: string;
}

const EMPTY_FORM: AddFormState = {
  serviceType: 'anodizing',
  otherLabel: '',
  vendorId: '',
  vendorName: '',
  // Default new services to lot-charge — that's the common KMF pattern for
  // outside vendors (anodize / heat treat / black oxide / grinding all bill
  // per batch, not per piece).
  costMode: 'lot_charge',
  setupCost: '',
  perPieceCost: '',
  lotCharge: '',
  supplierTaxPct: '',
};

export function OutsideServicesStep({ part, onChange }: StepProps) {
  const rows = part.outsideServices ?? [];

  // Vendor name cache so each row can render a stable name from vendorId
  // without hammering listVendors per render. Key = vendorId, value = name.
  const [vendorNameCache, setVendorNameCache] = useState<Record<string, string>>({});

  // Resolve any vendorIds we haven't seen yet → name. Fire-and-forget;
  // ignores stale responses on unmount via the abort flag.
  useEffect(() => {
    let cancelled = false;
    const missing = rows
      .map((r) => r.vendorId)
      .filter((id): id is string => !!id && !(id in vendorNameCache));
    if (missing.length === 0) return;
    (async () => {
      const next: Record<string, string> = {};
      for (const id of missing) {
        try {
          const v = await getVendor(id);
          if (v) next[id] = v.name;
        } catch {
          // Swallow — fallback rendering uses the legacy `vendor` string.
        }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setVendorNameCache((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rows, vendorNameCache]);

  function removeRow(i: number) {
    onChange({ outsideServices: rows.filter((_, idx) => idx !== i) });
  }

  function addServiceFromForm(form: AddFormState) {
    const labelFromTaxonomy =
      form.serviceType === 'other'
        ? form.otherLabel.trim() || 'Other'
        : VENDOR_SERVICE_LABELS[form.serviceType];
    const isLot = form.costMode === 'lot_charge';
    const newRow: OutsideService = {
      // legacy fields kept populated for backwards compatibility
      type: labelFromTaxonomy,
      vendor: form.vendorName.trim() || undefined,
      // preferred fields
      serviceType: form.serviceType,
      vendorId: form.vendorId || undefined,
      costMode: form.costMode,
      // Only persist the fields that belong to the chosen mode so the
      // pricing engine doesn't double-count or get confused.
      setupCost: isLot ? undefined : parseOptionalNumber(form.setupCost),
      perPieceCost: isLot ? undefined : parseOptionalNumber(form.perPieceCost),
      lotCharge: isLot ? parseOptionalNumber(form.lotCharge) : undefined,
      supplierTaxPct: parseOptionalNumber(form.supplierTaxPct),
    };
    onChange({ outsideServices: [...rows, newRow] });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No outside services yet. Add laser cut, heat treat, plating, paint, etc.
        </div>
      )}

      {rows.map((r, i) => (
        <ServiceRow
          key={i}
          index={i}
          row={r}
          vendorName={r.vendorId ? vendorNameCache[r.vendorId] : undefined}
          onRemove={() => removeRow(i)}
        />
      ))}

      <AddServiceForm onAdd={addServiceFromForm} />
    </div>
  );
}

// ============================================================================
// Row renderer
// ============================================================================

interface ServiceRowProps {
  index: number;
  row: OutsideService;
  vendorName: string | undefined;
  onRemove: () => void;
}

function ServiceRow({ index, row, vendorName, onRemove }: ServiceRowProps) {
  // Service-type label: prefer typed taxonomy when present, fall back to
  // legacy freeform `type` for old data.
  const serviceLabel = row.serviceType
    ? VENDOR_SERVICE_LABELS[row.serviceType]
    : row.type || '(no type)';
  // Vendor label: prefer the cached lookup (vendorId path), fall back to the
  // legacy freeform `vendor` string. Empty rendered as "—".
  const vendorLabel = vendorName ?? row.vendor ?? '—';

  return (
    <div className="border border-line rounded p-3 bg-bg/30">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-text-mute uppercase tracking-wide">
            #{index + 1}
          </span>
          <span className="inline-flex items-center rounded-full bg-purple/15 text-purple border border-purple/30 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
            {serviceLabel}
          </span>
          <span className="text-sm text-text">
            <span className="text-text-mute">via</span> <span className="font-semibold">{vendorLabel}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-text-mute hover:text-orange py-1 px-2"
        >
          ✕ Remove
        </button>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
        {row.costMode === 'lot_charge' ? (
          <>
            <RowFact label="Lot charge ($)" value={fmtMoney(row.lotCharge)} />
            <RowFact label="Mode" value="Lot charge" />
            <RowFact
              label="Supplier tax %"
              value={row.supplierTaxPct == null ? '—' : `${row.supplierTaxPct}%`}
            />
          </>
        ) : (
          <>
            <RowFact label="Setup ($)" value={fmtMoney(row.setupCost)} />
            <RowFact label="Per piece ($)" value={fmtMoney(row.perPieceCost)} />
            <RowFact
              label="Supplier tax %"
              value={row.supplierTaxPct == null ? '—' : `${row.supplierTaxPct}%`}
            />
          </>
        )}
      </div>
    </div>
  );
}

interface RowFactProps {
  label: string;
  value: string;
}

function RowFact({ label, value }: RowFactProps) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide">
        {label}
      </div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}

// ============================================================================
// Add-row inline form
// ============================================================================

interface AddServiceFormProps {
  onAdd: (form: AddFormState) => void;
}

function AddServiceForm({ onAdd }: AddServiceFormProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AddFormState>(EMPTY_FORM);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  // Refresh vendor list whenever the picked service type changes — narrows
  // to vendors who advertise that service.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setVendorsLoading(true);
    listVendors({ service: form.serviceType })
      .then((vs) => {
        if (!cancelled) setVendors(vs);
      })
      .catch(() => {
        if (!cancelled) setVendors([]);
      })
      .finally(() => {
        if (!cancelled) setVendorsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.serviceType]);

  // Reflect the selected vendor's name back into the form so the legacy
  // `vendor` string gets populated alongside `vendorId` on save.
  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === form.vendorId) ?? null,
    [vendors, form.vendorId],
  );
  useEffect(() => {
    if (selectedVendor) {
      setForm((f) => (f.vendorName === selectedVendor.name ? f : { ...f, vendorName: selectedVendor.name }));
    }
  }, [selectedVendor]);

  function handleAdd() {
    onAdd(form);
    setForm(EMPTY_FORM);
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add outside service
      </button>
    );
  }

  return (
    <div className="border-2 border-orange/40 rounded p-3 bg-orange/5 space-y-3">
      <div className="text-xs font-semibold text-navy uppercase tracking-wide">
        Add outside service
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Service type">
          <select
            value={form.serviceType}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                serviceType: e.target.value as VendorServiceType,
                // Reset vendor when service type changes — picker re-filters.
                vendorId: '',
                vendorName: '',
              }))
            }
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
          >
            {SERVICE_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {VENDOR_SERVICE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Vendor">
          <select
            value={form.vendorId}
            onChange={(e) =>
              setForm((f) => ({ ...f, vendorId: e.target.value }))
            }
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
            disabled={vendorsLoading}
          >
            <option value="">
              {vendorsLoading
                ? 'Loading vendors…'
                : vendors.length === 0
                ? '— no vendors yet —'
                : '— pick vendor —'}
            </option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.favorite ? '★ ' : ''}
                {v.name}
              </option>
            ))}
          </select>
          <a
            href="/settings#vendors"
            className="text-[11px] text-orange hover:underline mt-1 inline-block"
          >
            + Create new vendor →
          </a>
        </Field>
      </div>

      {/* When "Other" is picked, surface a freeform name input for the
          service so the user can type "Sandblast" or whatever the taxonomy
          doesn't cover. */}
      {form.serviceType === 'other' && (
        <Field label="Service name (freeform)">
          <input
            type="text"
            value={form.otherLabel}
            onChange={(e) => setForm((f) => ({ ...f, otherLabel: e.target.value }))}
            placeholder="e.g. Sandblast"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
      )}

      {/* Fallback vendor name input — used when no Vendor record exists yet
          (e.g. user hasn't created one). Keeps the legacy freeform path open. */}
      {!form.vendorId && (
        <Field label="Vendor name (if not in list yet)">
          <input
            type="text"
            value={form.vendorName}
            onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}
            placeholder='e.g. "Industrial Hard Chrome"'
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
      )}

      {/* Mode toggle — controls which cost fields show below. Lot charge is
          the KMF default (most outside vendors bill per batch, not per piece). */}
      <div>
        <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
          Cost mode
        </span>
        <div className="inline-flex rounded border border-line-dark overflow-hidden">
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, costMode: 'lot_charge' }))}
            className={`px-3 py-1.5 text-sm font-semibold ${
              form.costMode === 'lot_charge'
                ? 'bg-orange text-white'
                : 'bg-paper text-text hover:bg-bg'
            }`}
          >
            Lot charge
          </button>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, costMode: 'per_piece' }))}
            className={`px-3 py-1.5 text-sm font-semibold border-l border-line-dark ${
              form.costMode === 'per_piece'
                ? 'bg-orange text-white'
                : 'bg-paper text-text hover:bg-bg'
            }`}
          >
            Per piece
          </button>
        </div>
      </div>

      {form.costMode === 'lot_charge' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lot charge ($)">
            <input
              type="number"
              step="0.01"
              value={form.lotCharge}
              onChange={(e) => setForm((f) => ({ ...f, lotCharge: e.target.value }))}
              placeholder="125"
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
          <Field label="Supplier tax %">
            <input
              type="number"
              step="0.1"
              value={form.supplierTaxPct}
              onChange={(e) => setForm((f) => ({ ...f, supplierTaxPct: e.target.value }))}
              placeholder="0"
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Setup ($)">
            <input
              type="number"
              step="0.01"
              value={form.setupCost}
              onChange={(e) => setForm((f) => ({ ...f, setupCost: e.target.value }))}
              placeholder="25"
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
          <Field label="Per piece ($)">
            <input
              type="number"
              step="0.01"
              value={form.perPieceCost}
              onChange={(e) => setForm((f) => ({ ...f, perPieceCost: e.target.value }))}
              placeholder="3.75"
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
          <Field label="Supplier tax %">
            <input
              type="number"
              step="0.1"
              value={form.supplierTaxPct}
              onChange={(e) => setForm((f) => ({ ...f, supplierTaxPct: e.target.value }))}
              placeholder="0"
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => {
            setForm(EMPTY_FORM);
            setOpen(false);
          }}
          className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAdd}
          className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function parseOptionalNumber(s: string): number | undefined {
  if (s === '') return undefined;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}

function fmtMoney(n: number | undefined): string {
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

function Field({ label, children, className }: FieldProps) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
